import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Table as AntTable, Button, Space, message, Image, Tag, Input } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons'
import { Wrapper, Actions, StyledButton } from './ArcAd.styles'
import Search from 'components/Search'
import useAction from 'utils/useAction'
import { selectors as arcAdsSelectors } from 'domains/arcAds'
import * as actions from 'actions/views/arcAds'
import { formatArcAdForTable } from 'domains/arcAds/formatters'
import {
  openConfirmModal,
  openArcAdModal,
} from 'reducers/modals/actions'
import { FIELD_NAMES } from 'const/forms'
import { getColumns } from './columns'

const ArcAds = () => {
  const [filters, setFilters] = useState({})
  const [sorter, setSorter] = useState({})
  const [search, setSearch] = useState('')
  const ads = useSelector(arcAdsSelectors.getArcAds)
  const isLoaded = useSelector(arcAdsSelectors.getIsLoaded)
  const page = useSelector(arcAdsSelectors.getPage)
  const pageCount = useSelector(arcAdsSelectors.getPageCount)
  const searchString = useSelector(arcAdsSelectors.getSearchString)
  const isSaving = useSelector(arcAdsSelectors.getIsSavingActive)
  const isFiltered = useSelector(arcAdsSelectors.getIsDataFilteredOrSorted)
  const includedData = useSelector(arcAdsSelectors.getIncludedData)
  const fetchAds = useAction(actions.fetchArcAds)
  const deleteAd = useAction(actions.deleteArcAd)
  const resetFilters = useAction(actions.resetFiltersAndSorter)
  const openConfirmModalFunc = useAction(openConfirmModal)
  const openArcAdModalFunc = useAction(openArcAdModal)

  useEffect(() => {
    fetchAds({ page: 1, search: '', filters: {}, sorter: {sort: 'created_at'} })
  }, [fetchAds])

  const handleTableChange = (pagination, tableFilters, tableSorter) => {
    const newPage = pagination.current
    const newFilters = tableFilters

    setFilters(newFilters)
    fetchAds({
      page: newPage,
      search: search,
      filters: newFilters,
      sorter: {sort: 'created_at'}
    })
  }

  const handleSearch = useCallback((searchValue) => {
    setSearch(searchValue)
    fetchAds({
      page: 1,
      search: searchValue,
      filters,
      sorter: {sort: 'created_at'}
    })
  }, [fetchAds, filters, sorter])

  const handleDeleteAd = (ad) => {
    const callback = () => deleteAd({
      id: ad.id,
      callback: () => {
        fetchAds({ page, search: searchString, filters, sorter: {sort: 'created_at'} })
      }
    })
    const text =
      `Are you sure you want to delete ad (ID: ${ad.id})? ` + "This action can't be revoked."
    openConfirmModalFunc({ callback, text })
  }

  const handleCreateAd = () => {
    openArcAdModalFunc()
  }

  const handleEditAd = (ad) => {
    openArcAdModalFunc(ad)
  }

  const handleResetFilters = () => {
    setFilters({})
    setSorter({})
    setSearch('')
    resetFilters()
    fetchAds({ page: 1, search: '', filters: {}, sorter: { sort: 'created_at'} })
  }

  const dataSource = ads.map(ad => formatArcAdForTable(ad, includedData))

  return (
    <Wrapper>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Arc Ads</h1>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateAd}
            disabled={!isLoaded}
          >
            Create Ad
          </Button>
          <Button
            onClick={handleResetFilters}
            disabled={!isFiltered}
          >
            Reset Filters
          </Button>
        </Space>
      </div>

      <Search
        value={search}
        onSubmit={handleSearch}
        placeholder="Search ads..."
        enterButton
        style={{ marginBottom: 16 }}
      />

      <AntTable
        columns={getColumns(handleEditAd, handleDeleteAd)}
        dataSource={dataSource}
        rowKey="id"
        loading={!isLoaded || isSaving}
        onChange={handleTableChange}
        pagination={{
          current: page,
          total: pageCount * 20,
          pageSize: 20,
          showQuickJumper: true,
          showSizeChanger: false,
          position: ['bottomCenter']
        }}
        scroll={{ x: 1200 }}
        size="middle"
        bordered
      />
    </Wrapper>
  )
}

export default ArcAds